import React, {ReactNode, useMemo} from 'react';
import classNames from 'classnames';
import {useField} from 'formik';
import formDropdownStyles from '../../form-controls/form-dropdown/form-dropdown.module.scss';
import {DropdownWrapper, DropdownWrapperOption} from '../dropdown-wrapper';
import styles from './dropdown-field.module.scss';

type DropdownFieldItem = {
    id: string | number;
    name: ReactNode;
    disabled?: boolean;
};

type DropdownFieldProps<TItem extends DropdownFieldItem = DropdownFieldItem> = {
    label: string;
    name: string;
    items: TItem[];
    required?: boolean;
    placeholder?: string;
    description?: ReactNode;
    error?: ReactNode;
    onChange?: (item: TItem | null) => void;
    onFocus?: () => void;
    disabled?: boolean;
};

const getErrorMessage = (error: unknown): string | null => {
    if (typeof error === 'string') {
        return error;
    }

    if (Array.isArray(error)) {
        for (const item of error) {
            const message = getErrorMessage(item);
            if (message) {
                return message;
            }
        }
        return null;
    }

    if (error && typeof error === 'object') {
        for (const value of Object.values(error)) {
            const message = getErrorMessage(value);
            if (message) {
                return message;
            }
        }
    }

    return null;
};

const DropdownField = <TItem extends DropdownFieldItem>({
    label,
    name,
    items,
    required,
    placeholder,
    description,
    onChange,
    onFocus,
    disabled,
    error,
}: DropdownFieldProps<TItem>) => {
    const [field, meta, helpers] = useField<TItem | null>(name);
    const options: DropdownWrapperOption<TItem['id']>[] = useMemo(() => items.map(item => ({
        id: item.id,
        label: item.name,
        disabled: item.disabled
    })), [items]);
    const errorMessage = getErrorMessage(error) ?? getErrorMessage(meta.error);
    const hasError = Boolean(errorMessage);

    const handleChange = (_: unknown, value: TItem['id'] | null) => {
        const selected = items.find(item => item.id === value) ?? null;
        helpers.setValue(selected);
        onChange?.(selected);
    };

    return (
        <label className={`${formDropdownStyles.wrapper} ${styles.wrapper}`}>
            <div className={formDropdownStyles.labelRow}>
                <span className={`${formDropdownStyles.fieldName} ${styles.label}`}>{label}</span>
                {
                    required
                        ? <span className={formDropdownStyles.asterisk}>*</span>
                        : <span className={formDropdownStyles.optional}>(optional)</span>
                }
            </div>
            <DropdownWrapper
                className={classNames(formDropdownStyles.dropdown, {
                    [formDropdownStyles.errorState]: hasError,
                    'dropdown-error': hasError,
                })}
                name={name}
                value={(field.value?.id ?? null) as TItem['id'] | null}
                options={options}
                showCheckIcon
                placeholder={placeholder}
                disabled={disabled}
                onChange={(_, value) => handleChange(_, value as TItem['id'] | null)}
                onFocus={onFocus}
                onBlur={() => helpers.setTouched(true)}
            />
            {hasError
                ? <div className={formDropdownStyles.error}>{errorMessage}</div>
                : description
                    ? <span className={formDropdownStyles.description}>{description}</span>
                    : null}
        </label>
    );
};

export { DropdownField };
export type { DropdownFieldProps, DropdownFieldItem };
